import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import clsx from 'clsx';
import {
    Users,
    Calendar,
    Download,
    RotateCcw,
    Eye,
    Search,
    AlertTriangle,
    FileSpreadsheet,
    Info,
    RefreshCw,
    ChevronRight,
    UserX,
    Printer,
    FileDown,
    Clock
} from 'lucide-react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';

// Import slice actions and selectors
import {
    fetchEmpExit,
    fetchReportEmpExitById,
    setFilters,
    clearFilters,
    resetEmpExitData,
    clearError,
    resetEmpExitDetails,
    clearEmpExitListData,
    selectEmpExitList,
    selectEmpExitDetails,
    selectLoading,
    selectErrors,
    selectFilters,
    selectIsAnyLoading
} from '../../slices/HrReportSlice/employeeExitReportSlice';

// Import CustomDatePicker
import CustomDatePicker from '../../components/CustomDatePicker';

// Utility functions for date handling
const formatDateForAPI = (date) => {
    if (!date) return '';
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
    }
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
};

const formatDateForDisplay = (date) => {
    if (!date) return null;
    if (date instanceof Date) return date;
    if (typeof date === 'string') {
        const d = new Date(date);
        return isNaN(d.getTime()) ? null : d;
    }
    return null;
};

const convertDateToString = (date) => {
    if (!date) return '';
    if (typeof date === 'string') return date;
    if (date instanceof Date) {
        return formatDateForAPI(date);
    }
    return '';
};

// Tooltip Component
const Tooltip = ({ children, content }) => {
    const [showTooltip, setShowTooltip] = useState(false);

    return (
        <div
            className="relative inline-block"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
        >
            {children}
            {showTooltip && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 text-xs text-white bg-gray-900 dark:bg-gray-700 rounded-lg shadow-lg whitespace-nowrap z-50">
                    {content}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                </div>
            )}
        </div>
    );
};

// Exit Type Badge Component
const ExitTypeBadge = ({ status }) => {
    const statusConfig = {
        'Approved': { color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' },
        'Rejected': { color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' },
        'Pending': { color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' },
        'Default': { color: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300' }
    };

    const config = statusConfig[status] || statusConfig['Default'];

    return (
        <span className={clsx(
            "px-3 py-1 rounded-full text-xs font-semibold",
            config.color
        )}>
            {status || 'N/A'}
        </span>
    );
};

// Summary Cards Component
const SummaryCards = ({ empExitList }) => {
    if (!empExitList || !Array.isArray(empExitList.Data) || empExitList.Data.length === 0) {
        return null;
    }

    const data = empExitList.Data;

    // Calculate summary statistics
    const summary = {
        totalExits: data.length,
        approved: data.filter(emp => emp.ApprovalStatus === 'Approved').length,
        rejected: data.filter(emp => emp.ApprovalStatus === 'Rejected').length,
        pending: data.filter(emp => emp.ApprovalStatus === 'Pending').length,
        avgNoticePeriod: data.length > 0 
            ? (data.reduce((sum, emp) => sum + (parseFloat(emp.NoticePereiodDays) || 0), 0) / data.length).toFixed(1)
            : 0
    };

    const cards = [
        {
            title: 'Total Exits',
            value: summary.totalExits,
            icon: UserX,
            color: 'from-red-500 to-rose-600',
            bgColor: 'from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20'
        },
        {
            title: 'Approved',
            value: summary.approved,
            icon: Users,
            color: 'from-green-500 to-emerald-600',
            bgColor: 'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20'
        },
        {
            title: 'Rejected',
            value: summary.rejected,
            icon: AlertTriangle,
            color: 'from-red-500 to-orange-600',
            bgColor: 'from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20'
        },
        {
            title: 'Pending',
            value: summary.pending,
            icon: Clock,
            color: 'from-yellow-500 to-orange-600',
            bgColor: 'from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20'
        },
        {
            title: 'Avg Notice Period',
            value: `${summary.avgNoticePeriod} days`,
            icon: Calendar,
            color: 'from-indigo-500 to-purple-600',
            bgColor: 'from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20'
        }
    ];

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
            {cards.map((card, index) => (
                <div key={index} className={`bg-gradient-to-r ${card.bgColor} rounded-xl p-6 border border-gray-200 dark:border-gray-700`}>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">
                                {card.title}
                            </p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {card.value}
                            </p>
                        </div>
                        <div className={`bg-gradient-to-r ${card.color} p-3 rounded-lg`}>
                            <card.icon className="h-6 w-6 text-white" />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

// Full & Final helpers
// ====================
const fmtAmt = (val) => {
    const n = parseFloat(val);
    if (isNaN(n)) return '0.00';
    return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtWhole = (val) => {
    const n = parseFloat(val);
    return isNaN(n) ? '0' : Math.round(n).toString();
};

// Heads shown under "Other Earnings" instead of the monthly earnings column (mirrors legacy ExitDataView)
const OTHER_EARNING_HEADS = ['LEAVE ENCASHMENT', 'EMPLOYEE GRATUITY', 'BONUS BASIC'];
const EARNING_HEAD_TYPES = ['Earning', 'OtherBenefit', 'Benefit'];

// Monthly earnings (excluding the "Other Earnings" heads) and deductions
const splitSalaryHeads = (fs) => {
    const heads = fs.lstMonthSalaryHeads || [];
    return {
        earnings: heads.filter(h =>
            EARNING_HEAD_TYPES.includes(h.HeadType) &&
            !OTHER_EARNING_HEADS.includes((h.SalaryHead || '').toUpperCase())
        ),
        deductions: heads.filter(h => h.HeadType === 'Deduction')
    };
};

// Build a Blob URL from the raw DocBaseString (base64, no data: prefix).
// DocType comes back as 'Pdf' / 'Image' from spGetReportEmpExitbyId.
const getDocumentUrl = (docBaseString, docType) => {
    if (!docBaseString) return null;
    try {
        const byteCharacters = atob(docBaseString);
        const byteArray = new Uint8Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) byteArray[i] = byteCharacters.charCodeAt(i);
        const type = (docType || '').toLowerCase();
        let mimeType = 'application/octet-stream';
        if (type === 'pdf') mimeType = 'application/pdf';
        else if (['image', 'jpg', 'jpeg'].includes(type)) mimeType = 'image/jpeg';
        else if (type === 'png') mimeType = 'image/png';
        return URL.createObjectURL(new Blob([byteArray], { type: mimeType }));
    } catch (error) {
        return null;
    }
};

// Label / value row for the header info block
const InfoRow = ({ label, value }) => (
    <div className="flex text-sm">
        <span className="w-40 flex-shrink-0 text-gray-500 dark:text-gray-400">{label}</span>
        <span className="font-bold text-gray-900 dark:text-white uppercase break-words">: {value ?? 'N/A'}</span>
    </div>
);

// Full & Final settlement slip — same layout as legacy ExitDataView
const FullFinalSlip = ({ fs }) => {
    const ms = fs.MonthSalary || {};
    const { earnings, deductions } = splitSalaryHeads(fs);
    const rowCount = Math.max(earnings.length, deductions.length);
    const gratuity = parseFloat(fs.Gratuity) || 0;

    const darkCell = 'bg-[#202447] text-white';

    return (
        <div className="space-y-4 text-sm">
            {/* Earnings / Deductions */}
            <table className="w-full border border-gray-400 dark:border-gray-600">
                <thead>
                    <tr className={darkCell}>
                        <th colSpan={2} className="px-3 py-2 font-semibold text-center">
                            Salary For: {ms.PayRollFortheDate || fs.PayRollFortheDate || '—'}
                        </th>
                        <th colSpan={2} className="px-3 py-2 font-semibold text-center">
                            Paid Days: {ms.TotalSalaryDays != null ? fmtAmt(ms.TotalSalaryDays) : '—'}
                        </th>
                    </tr>
                    <tr className={clsx(darkCell, 'border-t border-white/40')}>
                        <th className="px-3 py-2 text-center">EARNINGS</th>
                        <th className="px-3 py-2 text-right border-r border-white/40">Rs</th>
                        <th className="px-3 py-2 text-center">DEDUCTIONS</th>
                        <th className="px-3 py-2 text-right">Rs</th>
                    </tr>
                </thead>
                <tbody className="text-xs text-gray-800 dark:text-gray-100">
                    {rowCount === 0 ? (
                        <tr>
                            <td colSpan={4} className="px-3 py-2 text-center text-gray-400">No salary heads</td>
                        </tr>
                    ) : Array.from({ length: rowCount }).map((_, i) => {
                        const e = earnings[i];
                        const d = deductions[i];
                        return (
                            <tr key={i} className="border-t border-gray-300 dark:border-gray-600">
                                <td className="px-2 py-1 uppercase">{e?.SalaryHead || ''}</td>
                                <td className="px-2 py-1 text-right border-r border-gray-300 dark:border-gray-600">{e ? fmtAmt(e.HeadAmount) : ''}</td>
                                <td className="px-2 py-1 uppercase">{d?.SalaryHead || ''}</td>
                                <td className="px-2 py-1 text-right">{d ? fmtAmt(d.HeadAmount) : ''}</td>
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot className="text-gray-900 dark:text-white">
                    <tr className="border-t border-gray-400 dark:border-gray-600">
                        <td className="px-3 py-2 text-center text-xs uppercase border-r border-gray-300 dark:border-gray-600">Gross</td>
                        <td className="px-3 py-2 text-right font-semibold border-r border-gray-300 dark:border-gray-600">{fmtAmt(fs.MonthGross)}</td>
                        <td className="px-3 py-2 text-center text-xs uppercase">Deduction</td>
                        <td className="px-3 py-2 text-right font-semibold">{fmtAmt(fs.MonthDeduction)}</td>
                    </tr>
                </tfoot>
            </table>

            {/* Other Earnings + totals */}
            <table className="w-full border border-gray-400 dark:border-gray-600">
                <thead className={darkCell}>
                    <tr>
                        <th colSpan={6} className="px-3 py-2 text-left font-semibold">Other Earnings</th>
                    </tr>
                    <tr className="border-t border-white/40 text-xs">
                        <th className="px-3 py-2 border-r border-white/40"></th>
                        <th className="px-3 py-2 text-center border-r border-white/40">Total Earned Basic</th>
                        <th className="px-3 py-2 text-center border-r border-white/40">Last Drawn Salary Excluding HRA And Daily Allowance</th>
                        <th className="px-3 py-2 text-center border-r border-white/40">For Days</th>
                        <th className="px-3 py-2 text-center border-r border-white/40">Per Day</th>
                        <th className="px-3 py-2 text-center">Amount</th>
                    </tr>
                </thead>
                <tbody className="text-gray-800 dark:text-gray-100 [&_td]:px-3 [&_td]:py-2 [&_td]:border [&_td]:border-gray-300 dark:[&_td]:border-gray-600">
                    <tr>
                        <td>Bonus Basic(8.33%)</td>
                        <td className="text-right">{fmtAmt(fs.TotalBasic)}</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td className="text-right">{fmtAmt(fs.BonusBasic)}</td>
                    </tr>
                    <tr>
                        <td>Leave Encashment</td>
                        <td></td>
                        <td className="text-right">{fmtAmt(fs.LastDrawnSalary)}</td>
                        <td className="text-right">{fmtWhole(ms.BalanceLeaves)}</td>
                        <td className="text-right">{fmtAmt(fs.LastDrawnSalaryPerDay)}</td>
                        <td className="text-right">{fmtAmt(fs.LeaveEncashment)}</td>
                    </tr>
                    <tr>
                        <td>Gratuity</td>
                        <td></td>
                        <td className="text-right">{fmtAmt(fs.LastDrawnSalary)}</td>
                        <td className="text-right">{fmtWhole(fs.Gratuitydays)}</td>
                        <td className="text-right">{fmtAmt(fs.GratuityPerday)}</td>
                        <td className="text-right">{fmtAmt(fs.Gratuity)}</td>
                    </tr>
                </tbody>
                <tfoot className={clsx(darkCell, 'font-semibold')}>
                    <tr>
                        <td colSpan={2} className="px-3 py-2.5 text-center">Total Earnings: {fmtAmt(fs.FinalGross)}</td>
                        <td colSpan={2} className="px-3 py-2.5 text-center">Total Deductions: {fmtAmt(fs.FinalDeduction)}</td>
                        <td colSpan={2} className="px-3 py-2.5 text-center">Net Due: {fmtAmt(fs.FinalNet)}</td>
                    </tr>
                    {gratuity > 0 && (
                        <tr className="border-t border-white/40">
                            <td colSpan={6} className="px-3 py-2.5 text-center">
                                Gratuity Released Directly From LIC Rs: {fmtAmt(fs.Gratuity)}
                            </td>
                        </tr>
                    )}
                    <tr className="border-t border-white/40">
                        <td colSpan={2} className="px-3 py-2.5 text-center">Payment Amount: {fmtAmt(fs.FinalNet)}</td>
                        <td colSpan={2}></td>
                        <td colSpan={2} className="px-3 py-2.5 text-center">Recovery Amount: {fmtAmt(fs.RecoverAmt)}</td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

// Full & Final export (PDF / Excel)
// =================================
const toNum = (val) => {
    const n = parseFloat(val);
    return isNaN(n) ? 0 : n;
};

const getFnfFileName = (details) =>
    `Full_Final_Settlement_${details.EmpRefNo || ''}_${(details.EmployeeName || '').trim().replace(/\s+/g, '_')}`;

// Header info shown above the slip, as [label, value] pairs
const getFnfInfo = (details) => {
    const fs = details.finalsalData;
    const info = [
        ['Employee Name', details.EmployeeName],
        ['Designation', details.DesignationName],
        ['Cost Center', details.CCName || details.CostCenter],
        ['Employee ID', details.EmpRefNo],
        ['Resignation Date', details.ResignationDate],
        ['Relieving Date', details.RelievingDate],
        ['Total Years Worked', fs.TotalExp]
    ];
    if (toNum(fs.Gratuity) > 0) info.push(['Gratuity Pay By', fs.GratuityPayBy]);
    return info.map(([label, value]) => [label, value ?? 'N/A']);
};

const downloadFnfExcel = (details) => {
    const fs = details.finalsalData;
    const ms = fs.MonthSalary || {};
    const { earnings, deductions } = splitSalaryHeads(fs);
    const rowCount = Math.max(earnings.length, deductions.length);
    const info = getFnfInfo(details);

    // Day counts are whole numbers; every other number is an amount
    const days = (val) => ({ t: 'n', v: Math.round(toNum(val)), z: '0' });

    const rows = [['FULL & FINAL SETTLEMENT'], []];
    for (let i = 0; i < info.length; i += 2) {
        rows.push([info[i][0], info[i][1], '', info[i + 1]?.[0] || '', info[i + 1]?.[1] || '']);
    }
    rows.push(['Remarks', details.Remarks ?? 'N/A']);
    rows.push([]);

    rows.push([
        `Salary For: ${ms.PayRollFortheDate || fs.PayRollFortheDate || '—'}`, '',
        `Paid Days: ${ms.TotalSalaryDays != null ? fmtAmt(ms.TotalSalaryDays) : '—'}`
    ]);
    rows.push(['EARNINGS', 'Rs', 'DEDUCTIONS', 'Rs']);
    for (let i = 0; i < rowCount; i++) {
        const e = earnings[i];
        const d = deductions[i];
        rows.push([e?.SalaryHead || '', e ? toNum(e.HeadAmount) : '', d?.SalaryHead || '', d ? toNum(d.HeadAmount) : '']);
    }
    rows.push(['GROSS', toNum(fs.MonthGross), 'DEDUCTION', toNum(fs.MonthDeduction)]);
    rows.push([]);

    rows.push(['Other Earnings']);
    rows.push(['', 'Total Earned Basic', 'Last Drawn Salary Excluding HRA And Daily Allowance', 'For Days', 'Per Day', 'Amount']);
    rows.push(['Bonus Basic(8.33%)', toNum(fs.TotalBasic), '', '', '', toNum(fs.BonusBasic)]);
    rows.push(['Leave Encashment', '', toNum(fs.LastDrawnSalary), days(ms.BalanceLeaves), toNum(fs.LastDrawnSalaryPerDay), toNum(fs.LeaveEncashment)]);
    rows.push(['Gratuity', '', toNum(fs.LastDrawnSalary), days(fs.Gratuitydays), toNum(fs.GratuityPerday), toNum(fs.Gratuity)]);
    rows.push([]);

    rows.push(['Total Earnings', toNum(fs.FinalGross), 'Total Deductions', toNum(fs.FinalDeduction), 'Net Due', toNum(fs.FinalNet)]);
    if (toNum(fs.Gratuity) > 0) {
        rows.push(['Gratuity Released Directly From LIC Rs', toNum(fs.Gratuity)]);
    }
    rows.push(['Payment Amount', toNum(fs.FinalNet), '', '', 'Recovery Amount', toNum(fs.RecoverAmt)]);

    const ws = XLSX.utils.aoa_to_sheet(rows);
    Object.keys(ws).forEach((ref) => {
        const cell = ws[ref];
        if (ref[0] !== '!' && cell.t === 'n' && !cell.z) cell.z = '#,##0.00';
    });
    ws['!cols'] = [{ wch: 36 }, { wch: 18 }, { wch: 30 }, { wch: 16 }, { wch: 18 }, { wch: 16 }];
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Full & Final');
    XLSX.writeFile(wb, `${getFnfFileName(details)}.xlsx`);
};

// Draws table rows on a jsPDF doc. Each row is an array of cells:
// { text, span, align: 'left'|'center'|'right', dark, bold }. Returns the next y.
const PDF_MARGIN = 12;
const PDF_DARK = [32, 36, 71]; // #202447, matches the on-screen slip
const drawPdfRows = (doc, y, widths, rows) => {
    const pad = 1.6;
    const lineH = 3.6;
    const pageH = doc.internal.pageSize.getHeight();

    rows.forEach((row) => {
        let col = 0;
        const cells = row.map((c) => {
            const span = c.span || 1;
            const x = PDF_MARGIN + widths.slice(0, col).reduce((a, b) => a + b, 0);
            const w = widths.slice(col, col + span).reduce((a, b) => a + b, 0);
            col += span;
            doc.setFont('helvetica', c.bold || c.dark ? 'bold' : 'normal');
            return { ...c, x, w, lines: doc.splitTextToSize(String(c.text ?? ''), w - pad * 2) };
        });
        const h = Math.max(1, ...cells.map(c => c.lines.length)) * lineH + pad * 2;

        if (y + h > pageH - PDF_MARGIN) {
            doc.addPage();
            y = PDF_MARGIN;
        }

        cells.forEach((c) => {
            doc.setDrawColor(150);
            if (c.dark) {
                doc.setFillColor(...PDF_DARK);
                doc.rect(c.x, y, c.w, h, 'FD');
                doc.setTextColor(255);
            } else {
                doc.rect(c.x, y, c.w, h);
                doc.setTextColor(30);
            }
            doc.setFont('helvetica', c.bold || c.dark ? 'bold' : 'normal');
            const align = c.align || 'left';
            const tx = align === 'right' ? c.x + c.w - pad : align === 'center' ? c.x + c.w / 2 : c.x + pad;
            doc.text(c.lines, tx, y + pad + lineH * 0.75, { align });
        });
        y += h;
    });
    return y;
};

const downloadFnfPdf = (details) => {
    const fs = details.finalsalData;
    const ms = fs.MonthSalary || {};
    const { earnings, deductions } = splitSalaryHeads(fs);
    const rowCount = Math.max(earnings.length, deductions.length);

    const doc = new jsPDF('p', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const contentW = pageW - PDF_MARGIN * 2;
    let y = PDF_MARGIN + 4;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(30);
    doc.text('FULL & FINAL SETTLEMENT', pageW / 2, y, { align: 'center' });
    y += 6;

    // Employee info, two label/value pairs per row
    doc.setFontSize(8);
    const info = getFnfInfo(details);
    const infoRows = [];
    for (let i = 0; i < info.length; i += 2) {
        const right = info[i + 1];
        infoRows.push([
            { text: info[i][0] },
            { text: String(info[i][1]).toUpperCase(), bold: true },
            { text: right ? right[0] : '' },
            { text: right ? String(right[1]).toUpperCase() : '', bold: true }
        ]);
    }
    infoRows.push([{ text: 'Remarks' }, { text: String(details.Remarks ?? 'N/A').toUpperCase(), bold: true, span: 3 }]);
    y = drawPdfRows(doc, y, [32, 61, 32, 61], infoRows) + 5;

    // Earnings / Deductions
    const edRows = [
        [
            { text: `Salary For: ${ms.PayRollFortheDate || fs.PayRollFortheDate || '—'}`, span: 2, align: 'center', dark: true },
            { text: `Paid Days: ${ms.TotalSalaryDays != null ? fmtAmt(ms.TotalSalaryDays) : '—'}`, span: 2, align: 'center', dark: true }
        ],
        [
            { text: 'EARNINGS', align: 'center', dark: true },
            { text: 'Rs', align: 'right', dark: true },
            { text: 'DEDUCTIONS', align: 'center', dark: true },
            { text: 'Rs', align: 'right', dark: true }
        ]
    ];
    if (rowCount === 0) {
        edRows.push([{ text: 'No salary heads', span: 4, align: 'center' }]);
    }
    for (let i = 0; i < rowCount; i++) {
        const e = earnings[i];
        const d = deductions[i];
        edRows.push([
            { text: (e?.SalaryHead || '').toUpperCase() },
            { text: e ? fmtAmt(e.HeadAmount) : '', align: 'right' },
            { text: (d?.SalaryHead || '').toUpperCase() },
            { text: d ? fmtAmt(d.HeadAmount) : '', align: 'right' }
        ]);
    }
    edRows.push([
        { text: 'GROSS', align: 'center' },
        { text: fmtAmt(fs.MonthGross), align: 'right', bold: true },
        { text: 'DEDUCTION', align: 'center' },
        { text: fmtAmt(fs.MonthDeduction), align: 'right', bold: true }
    ]);
    y = drawPdfRows(doc, y, [63, 30, 63, 30], edRows) + 5;

    // Other Earnings + totals
    const hdr = (text) => ({ text, align: 'center', dark: true });
    const amt = (text) => ({ text, align: 'right' });
    const otherRows = [
        [{ text: 'Other Earnings', span: 6, dark: true }],
        [hdr(''), hdr('Total Earned Basic'), hdr('Last Drawn Salary Excluding HRA And Daily Allowance'), hdr('For Days'), hdr('Per Day'), hdr('Amount')],
        [{ text: 'Bonus Basic(8.33%)' }, amt(fmtAmt(fs.TotalBasic)), amt(''), amt(''), amt(''), amt(fmtAmt(fs.BonusBasic))],
        [{ text: 'Leave Encashment' }, amt(''), amt(fmtAmt(fs.LastDrawnSalary)), amt(fmtWhole(ms.BalanceLeaves)), amt(fmtAmt(fs.LastDrawnSalaryPerDay)), amt(fmtAmt(fs.LeaveEncashment))],
        [{ text: 'Gratuity' }, amt(''), amt(fmtAmt(fs.LastDrawnSalary)), amt(fmtWhole(fs.Gratuitydays)), amt(fmtAmt(fs.GratuityPerday)), amt(fmtAmt(fs.Gratuity))],
        [
            { text: `Total Earnings: ${fmtAmt(fs.FinalGross)}`, span: 2, align: 'center', dark: true },
            { text: `Total Deductions: ${fmtAmt(fs.FinalDeduction)}`, span: 2, align: 'center', dark: true },
            { text: `Net Due: ${fmtAmt(fs.FinalNet)}`, span: 2, align: 'center', dark: true }
        ]
    ];
    if (toNum(fs.Gratuity) > 0) {
        otherRows.push([{ text: `Gratuity Released Directly From LIC Rs: ${fmtAmt(fs.Gratuity)}`, span: 6, align: 'center', dark: true }]);
    }
    otherRows.push([
        { text: `Payment Amount: ${fmtAmt(fs.FinalNet)}`, span: 2, align: 'center', dark: true },
        { text: '', span: 2, dark: true },
        { text: `Recovery Amount: ${fmtAmt(fs.RecoverAmt)}`, span: 2, align: 'center', dark: true }
    ]);
    drawPdfRows(doc, y, [36, 28, 50, 22, 24, contentW - 160], otherRows);

    doc.save(`${getFnfFileName(details)}.pdf`);
};

// Employee Exit Details Modal
const EmployeeExitDetailsModal = ({ isOpen, onClose, details }) => {
    if (!isOpen || !details) return null;

    const fs = details.finalsalData;
    const hasFullFinal = details.FinalPayrollStatus === 'Exist' && fs && fs.MonthSalary;
    const checklist = details.lstCheck || [];

    const handleViewDocument = () => {
        const url = details.DocURL || getDocumentUrl(details.DocBaseString, details.DocType); // DocURL = file on S3
        if (!url) {
            toast.error('Failed to load document');
            return;
        }
        window.open(url, '_blank');
    };

    const handleFnfDownload = (download, label) => {
        try {
            download(details);
            toast.success(`Full & Final settlement ${label} downloaded`);
        } catch (error) {
            toast.error(`Failed to generate ${label}`);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-indigo-600 to-purple-700 p-6 rounded-t-xl">
                    <div className="flex items-center justify-between">
                        <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                            <UserX className="h-7 w-7" />
                            Employee Exit Details
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-white hover:text-gray-200 transition-colors"
                        >
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Modal Body */}
                <div className="p-6 space-y-6">
                    {/* Employee / exit info */}
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg p-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                            <InfoRow label="Employee Name" value={details.EmployeeName} />
                            <InfoRow label="Designation" value={details.DesignationName} />
                            <InfoRow label="Cost Center" value={details.CCName || details.CostCenter} />
                            <InfoRow label="Employee ID" value={details.EmpRefNo} />
                            <InfoRow label="Resignation Date" value={details.ResignationDate} />
                            <InfoRow label="Relieving Date" value={details.RelievingDate} />
                            {hasFullFinal && <InfoRow label="Total Years Worked" value={fs.TotalExp} />}
                            {hasFullFinal && parseFloat(fs.Gratuity) > 0 && (
                                <InfoRow label="Gratuity Pay By" value={fs.GratuityPayBy} />
                            )}
                        </div>
                        <div className="mt-1">
                            <InfoRow label="Remarks" value={details.Remarks} />
                        </div>
                    </div>

                    {/* Full & Final settlement */}
                    <div>
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                            <FileSpreadsheet className="h-4 w-4 text-indigo-600" />
                            Full &amp; Final Settlement
                        </h3>
                        {hasFullFinal ? (
                            <FullFinalSlip fs={fs} />
                        ) : (
                            <div className="px-4 py-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-700 dark:text-amber-300">
                                Full &amp; Final settlement has not been approved for this employee yet.
                            </div>
                        )}
                    </div>

                    {/* Exit checklist */}
                    {checklist.length > 0 && (
                        <div>
                            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Exit Checklist</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {checklist.map((item) => (
                                    <label key={item.CheckId} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                                        <input
                                            type="checkbox"
                                            checked={item.Status === 'Yes'}
                                            disabled
                                            className="h-4 w-4 accent-indigo-700"
                                        />
                                        {item.Description}
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Supporting document */}
                    {details.DocType && (details.DocURL || details.DocBaseString) && (
                        <div className="flex items-center justify-between px-4 py-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                Exit supporting document ({details.DocType})
                            </span>
                            <button
                                onClick={handleViewDocument}
                                className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs font-medium rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-300"
                            >
                                <Eye className="h-4 w-4 mr-1" />
                                View Document
                            </button>
                        </div>
                    )}
                </div>

                {/* Modal Footer */}
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-b-xl flex flex-wrap justify-end gap-3">
                    {hasFullFinal && (
                        <>
                            <button
                                onClick={() => handleFnfDownload(downloadFnfPdf, 'PDF')}
                                className="px-4 py-2 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-lg hover:from-red-700 hover:to-rose-700 flex items-center gap-2 transition-all duration-300"
                            >
                                <Download className="h-4 w-4" />
                                F&amp;F PDF
                            </button>
                            <button
                                onClick={() => handleFnfDownload(downloadFnfExcel, 'Excel')}
                                className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 flex items-center gap-2 transition-all duration-300"
                            >
                                <FileSpreadsheet className="h-4 w-4" />
                                F&amp;F Excel
                            </button>
                        </>
                    )}
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-700 hover:to-gray-800 transition-all duration-300"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

// Helper function to download data as Excel
const downloadAsExcel = (data, filename) => {
    try {
        const csvContent = convertToCSV(data);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${filename}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    } catch (error) {
        toast.error('Error downloading Excel file');
    }
};

const convertToCSV = (data) => {
    if (!Array.isArray(data) || data.length === 0) return '';

    const headers = Object.keys(data[0]);
    const csvContent = [
        headers.join(','),
        ...data.map(row =>
            headers.map(header => {
                const value = row[header];
                return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
            }).join(',')
        )
    ].join('\n');

    return csvContent;
};

// Print function
const handlePrint = () => {
    window.print();
};

const EmployeeExitReportPage = () => {
    const dispatch = useDispatch();

    // Redux selectors
    const empExitList = useSelector(selectEmpExitList);
    const empExitDetails = useSelector(selectEmpExitDetails);
    const loading = useSelector(selectLoading);
    const errors = useSelector(selectErrors);
    const filters = useSelector(selectFilters);
    const isAnyLoading = useSelector(selectIsAnyLoading);

    // Local state for form inputs
    const [localFilters, setLocalFilters] = useState({
        fromDate: '',
        toDate: ''
    });

    // Modal state
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    // Set default dates to current month
    useEffect(() => {
        const currentDate = new Date();
        const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

        const formatDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        setLocalFilters({
            fromDate: formatDate(firstDay),
            toDate: formatDate(lastDay)
        });
    }, []);

    // Sync local filters with Redux filters
    useEffect(() => {
        if (filters.fromDate || filters.toDate) {
            setLocalFilters({
                fromDate: filters.fromDate,
                toDate: filters.toDate
            });
        }
    }, [filters]);

    // Show error messages via toast
    useEffect(() => {
        Object.entries(errors).forEach(([key, error]) => {
            if (error && error !== null) {
                toast.error(`Error with ${key}: ${error}`);
                dispatch(clearError({ errorType: key }));
            }
        });
    }, [errors, dispatch]);

    // Handle date changes
    const handleDateChange = (filterName, dateValue) => {
        const dateString = convertDateToString(dateValue);
        setLocalFilters(prev => ({
            ...prev,
            [filterName]: dateString
        }));
    };

    // Handle view button click
    const handleView = async () => {
        // Validation
        if (!localFilters.fromDate) {
            toast.warning('Please select From Date');
            return;
        }

        if (!localFilters.toDate) {
            toast.warning('Please select To Date');
            return;
        }

        // Validate date range
        const fromDate = new Date(localFilters.fromDate);
        const toDate = new Date(localFilters.toDate);

        if (fromDate > toDate) {
            toast.warning('From Date cannot be greater than To Date');
            return;
        }

        try {
            // Update Redux filters
            dispatch(setFilters(localFilters));

            // Prepare parameters for API call
            const params = {
                fromDate: localFilters.fromDate,
                toDate: localFilters.toDate
            };


            await dispatch(fetchEmpExit(params)).unwrap();

            toast.success('Employee exit report loaded successfully');

        } catch (error) {
            toast.error('Failed to fetch employee exit report. Please try again.');
        }
    };

    // Handle reset
    const handleReset = () => {
        const currentDate = new Date();
        const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

        const formatDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        setLocalFilters({
            fromDate: formatDate(firstDay),
            toDate: formatDate(lastDay)
        });

        dispatch(clearFilters());
        dispatch(resetEmpExitData());
    };

    // Handle view details
    const handleViewDetails = async (employee) => {
        try {

            // Fetch detailed report using employee data
            const params = {
                empRefNo: employee.EmpRefNo,
                id: employee.Id,
                roleId: employee.RoleId || '0'
            };


            const result = await dispatch(fetchReportEmpExitById(params)).unwrap();

            setSelectedEmployee(result?.Data || result);
            setIsDetailsModalOpen(true);

        } catch (error) {
            toast.error('Failed to fetch employee details. Please try again.');
        }
    };

    // Handle Excel download
    const handleExcelDownload = () => {
        try {
            if (!empExitList?.Data || empExitList.Data.length === 0) {
                toast.warning('No data available to download');
                return;
            }

            const data = empExitList.Data;
            const filename = `Employee_Exit_Report_${localFilters.fromDate}_to_${localFilters.toDate}`;

            downloadAsExcel(data, filename);
            toast.success('Excel file downloaded successfully');

        } catch (error) {
            toast.error('Excel download failed. Please try again.');
        }
    };

    return (
        <div className="space-y-6 p-6">
            {/* Page Header */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700 transition-colors">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                            <UserX className="h-8 w-8 text-indigo-600" />
                            Employee Exit Report
                        </h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                            Track and manage employee exits, resignations, and terminations
                        </p>
                    </div>
                    <div className="flex items-center space-x-3">
                        <div className="px-4 py-2 bg-gradient-to-r from-indigo-100 to-purple-100 dark:from-indigo-900 dark:to-purple-900 text-indigo-800 dark:text-indigo-200 text-sm rounded-full transition-colors">
                            HR Reports
                        </div>
                        {isAnyLoading && (
                            <div className="flex items-center space-x-2 text-indigo-600 dark:text-indigo-400">
                                <RefreshCw className="w-4 h-4 animate-spin" />
                                <span className="text-sm">Loading...</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Breadcrumb */}
                <nav className="text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex items-center space-x-2">
                        <span>Dashboard</span>
                        <ChevronRight className="w-4 h-4" />
                        <span>HR Reports</span>
                        <ChevronRight className="w-4 h-4" />
                        <span className="text-gray-900 dark:text-white">Employee Exit Report</span>
                    </div>
                </nav>
            </div>

            {/* Filters Section */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700 transition-colors">
                <div className="grid gap-6 mb-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
                    {/* From Date */}
                    <div>
                        <CustomDatePicker
                            label="From Date"
                            placeholder="Select from date"
                            value={formatDateForDisplay(localFilters.fromDate)}
                            onChange={(date) => handleDateChange('fromDate', date)}
                            maxDate={formatDateForDisplay(localFilters.toDate) || new Date()}
                            size="md"
                            required
                        />
                    </div>

                    {/* To Date */}
                    <div>
                        <CustomDatePicker
                            label="To Date"
                            placeholder="Select to date"
                            value={formatDateForDisplay(localFilters.toDate)}
                            onChange={(date) => handleDateChange('toDate', date)}
                            minDate={formatDateForDisplay(localFilters.fromDate)}
                            maxDate={new Date()}
                            size="md"
                            required
                        />
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex gap-3">
                        <button
                            onClick={handleView}
                            disabled={isAnyLoading || !localFilters.fromDate || !localFilters.toDate}
                            className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all duration-300 shadow-lg hover:shadow-xl"
                        >
                            {isAnyLoading ? (
                                <RotateCcw className="h-5 w-5 animate-spin" />
                            ) : (
                                <Eye className="h-5 w-5" />
                            )}
                            View Report
                        </button>
                        <button
                            onClick={handleReset}
                            className="px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-700 hover:to-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center gap-2 transition-all duration-300 shadow-lg hover:shadow-xl"
                        >
                            <RotateCcw className="h-5 w-5" />
                            Reset
                        </button>
                    </div>

                    <div className="flex items-center gap-4">
                        <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">Export:</span>

                        {/* Print Button */}
                        <Tooltip content="Print employee exit report">
                            <button
                                onClick={handlePrint}
                                disabled={!empExitList?.Data || empExitList.Data.length === 0}
                                className="px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all duration-300 shadow-lg hover:shadow-xl"
                            >
                                <Printer className="h-5 w-5" />
                                Print
                            </button>
                        </Tooltip>

                        {/* Excel Download Button */}
                        <Tooltip content="Download employee exit report as Excel file">
                            <button
                                onClick={handleExcelDownload}
                                disabled={!empExitList?.Data || empExitList.Data.length === 0}
                                className="px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all duration-300 shadow-lg hover:shadow-xl"
                            >
                                <FileDown className="h-5 w-5" />
                                Export Excel
                            </button>
                        </Tooltip>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <SummaryCards empExitList={empExitList} />

            {/* Employee Exit List Table */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors">
                {empExitList?.Data && Array.isArray(empExitList.Data) && empExitList.Data.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gradient-to-r from-indigo-600 to-purple-700">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                                        #
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                                        Employee Details
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                                        Designation / Cost Center
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                                        Resignation Date
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                                        Relieving Date
                                    </th>
                                    <th className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wider">
                                        Notice Period
                                    </th>
                                    <th className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {empExitList.Data.map((employee, index) => (
                                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold">
                                                {index + 1}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                <div className="text-sm font-bold text-gray-900 dark:text-white">
                                                    {employee.EmployeeName}
                                                </div>
                                                <div className="text-xs text-gray-600 dark:text-gray-400">
                                                    ID: {employee.EmpRefNo}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-900 dark:text-white">
                                                {employee.DesignationName || 'N/A'}
                                            </div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                                {employee.CCName || employee.CostCenter || 'N/A'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-red-600 dark:text-red-400 font-semibold">
                                                {employee.ResignationDate || 'N/A'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 dark:text-white">
                                                {employee.RelievingDate || 'N/A'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                                                {employee.NoticePereiodDays} {employee.NoticePereiodDays === 1 ? 'day' : 'days'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <ExitTypeBadge status={employee.ApprovalStatus} />
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <Tooltip content="View employee exit details">
                                                <button
                                                    onClick={() => handleViewDetails(employee)}
                                                    className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs font-medium rounded-lg hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-300"
                                                >
                                                    <Eye className="h-4 w-4 mr-1" />
                                                    View
                                                </button>
                                            </Tooltip>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Footer with count */}
                        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-700 dark:text-gray-300 font-medium">
                                    Showing {empExitList.Data.length} of {empExitList.Data.length} employee exits
                                </span>
                                <span className="text-indigo-600 dark:text-indigo-400 font-semibold">
                                    Exit Report: {localFilters.fromDate} to {localFilters.toDate}
                                </span>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Empty State */}
                        {!isAnyLoading && (
                            <div className="p-12 text-center">
                                <div className="flex flex-col items-center">
                                    <div className="bg-gradient-to-r from-indigo-100 to-purple-100 dark:from-indigo-900 dark:to-purple-900 rounded-full p-4 mb-4">
                                        <Search className="h-12 w-12 text-indigo-600 dark:text-indigo-400" />
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">No Employee Exit Data Found</h3>
                                    <p className="text-gray-600 dark:text-gray-400 max-w-md">
                                        Select a date range and click "View Report" to load employee exit data.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Loading State */}
                        {isAnyLoading && (
                            <div className="p-12 text-center">
                                <div className="flex flex-col items-center">
                                    <div className="bg-gradient-to-r from-indigo-100 to-purple-100 dark:from-indigo-900 dark:to-purple-900 rounded-full p-4 mb-4">
                                        <RotateCcw className="h-12 w-12 text-indigo-500 animate-spin" />
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Loading Employee Exit Report</h3>
                                    <p className="text-gray-600 dark:text-gray-400">
                                        Fetching employee exit data for the selected date range...
                                    </p>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Information Note */}
            <div className="bg-gradient-to-r from-indigo-50 to-cyan-50 dark:from-indigo-900/20 dark:to-cyan-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4">
                <div className="flex items-center gap-3">
                    <Info className="h-5 w-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                    <div className="text-indigo-800 dark:text-indigo-200 text-sm">
                        <p className="font-semibold mb-1">Employee Exit Report Features:</p>
                        <p className="text-gray-600 dark:text-indigo-200">
                            1. <strong>Date Range Filter:</strong> Select from and to dates to view employee exits within that period<br />
                            2. <strong>Approval Status:</strong> Track Approved, Rejected, and Pending exit requests<br />
                            3. <strong>Summary Statistics:</strong> View total exits, approval status breakdown, and average notice period<br />
                            4. <strong>Detailed View:</strong> Click "View" button to see complete employee exit details including dates, remarks, and approval status<br />
                            5. <strong>Export Options:</strong> Download the report as Excel or print for records<br />
                            6. <strong>Comprehensive Data:</strong> Track resignation date, relieving date, notice period, and approval status
                        </p>
                    </div>
                </div>
            </div>

            {/* Error Display */}
            {Object.values(errors).some(error => error) && (
                <div className="bg-gradient-to-r from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex items-center">
                        <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
                        <span className="text-red-800 dark:text-red-200 text-sm">
                            {Object.values(errors).find(error => error)}
                        </span>
                    </div>
                </div>
            )}

            {/* Employee Exit Details Modal */}
            <EmployeeExitDetailsModal
                isOpen={isDetailsModalOpen}
                onClose={() => {
                    setIsDetailsModalOpen(false);
                    setSelectedEmployee(null);
                }}
                details={selectedEmployee}
            />
        </div>
    );
};

export default EmployeeExitReportPage;